import React, { useState, useEffect } from 'react';
import { X, Instagram, Facebook, Mail, ChevronDown } from 'lucide-react';

const API_URL = 'https://annettenielsen.art/api';

function App() {
  const [artworks, setArtworks] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [sortBy, setSortBy] = useState('recommended');
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const response = await fetch(`${API_URL}/artworks`);
      const data = await response.json();
      setArtworks(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      setLoading(false);
    }
  };

  const sortedArtworks = [...artworks].sort((a, b) => {
    if (sortBy === 'recommended') {
      return a.display_order - b.display_order;
    }
    return new Date(b.date) - new Date(a.date);
  });

  const displayedArtworks = showAll ? sortedArtworks : sortedArtworks.slice(0, 10);
  const heroArtwork = sortedArtworks[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-100">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Fullscreen Artwork */}
      {heroArtwork && (
        <div className="relative h-screen w-full">
          <img
            src={heroArtwork.image_url}
            alt={heroArtwork.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
            <ChevronDown className="w-8 h-8 text-white drop-shadow-lg" />
          </div>
        </div>
      )}

      {/* Artist Section */}
      <section className="max-w-4xl mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="order-2 md:order-1">
            <h1 className="text-3xl md:text-4xl font-light mb-4 text-neutral-800">
              Annette Nielsen
            </h1>
            <div className="prose prose-neutral">
              <p className="text-neutral-600 leading-relaxed">
                Through my art, I explore the intersection of light, texture, and emotion. 
                Each piece is a meditation on the beauty found in everyday moments, 
                transformed through color and form into something that speaks to the soul.
              </p>
              <p className="text-neutral-600 leading-relaxed mt-4">
                My work invites viewers to pause, reflect, and find their own meaning 
                within the layers of paint and composition.
              </p>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <img
              src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=600&fit=crop"
              alt="Annette Nielsen"
              className="w-full aspect-square object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Artworks Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <h2 className="text-2xl font-light text-neutral-800">Artworks</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setSortBy('recommended')}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                sortBy === 'recommended'
                  ? 'bg-neutral-800 text-white'
                  : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
              }`}
            >
              Recommended
            </button>
            <button
              onClick={() => setSortBy('date')}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                sortBy === 'date'
                  ? 'bg-neutral-800 text-white'
                  : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
              }`}
            >
              By Date
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {displayedArtworks.map((artwork) => (
            <div
              key={artwork.id}
              onClick={() => setSelectedArtwork(artwork)}
              className="cursor-pointer group"
            >
              <div className="aspect-square overflow-hidden rounded-lg bg-neutral-100 mb-3">
                <img
                  src={artwork.image_url}
                  alt={artwork.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="font-medium text-neutral-800 mb-1">{artwork.title}</h3>
              <p className="text-sm text-neutral-500">
                {new Date(artwork.date).getFullYear()}
              </p>
            </div>
          ))}
        </div>

        {!showAll && sortedArtworks.length > 10 && (
          <div className="text-center mt-12">
            <button
              onClick={() => setShowAll(true)}
              className="px-8 py-3 bg-neutral-800 text-white rounded-full hover:bg-neutral-700 transition-colors"
            >
              Show All Artworks ({sortedArtworks.length})
            </button>
          </div>
        )}
      </section>

      {/* Social Media Section */}
      <section className="bg-neutral-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-xl font-light mb-6 text-neutral-800">Connect</h3>
          <div className="flex justify-center gap-6">
            <a
              href="https://instagram.com/annettenielsen.art"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white rounded-full hover:bg-neutral-100 transition-colors"
            >
              <Instagram className="w-6 h-6 text-neutral-700" />
            </a>
            <a
              href="https://facebook.com/annettenielsen.art"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white rounded-full hover:bg-neutral-100 transition-colors"
            >
              <Facebook className="w-6 h-6 text-neutral-700" />
            </a>
            <a
              href="mailto:an14on@gmail.com"
              className="p-3 bg-white rounded-full hover:bg-neutral-100 transition-colors"
            >
              <Mail className="w-6 h-6 text-neutral-700" />
            </a>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {selectedArtwork && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArtwork(null)}
        >
          <button
            onClick={() => setSelectedArtwork(null)}
            className="absolute top-4 right-4 p-2 bg-white bg-opacity-10 rounded-full hover:bg-opacity-20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedArtwork.image_url}
              alt={selectedArtwork.title}
              className="w-full h-auto max-h-[80vh] object-contain mb-6"
            />
            <div className="text-white text-center px-4">
              <h2 className="text-2xl md:text-3xl font-light mb-2">
                {selectedArtwork.title}
              </h2>
              <p className="text-neutral-300 mb-4">
                {new Date(selectedArtwork.date).getFullYear()}
              </p>
              {selectedArtwork.description && (
                <p className="text-neutral-200 max-w-2xl mx-auto leading-relaxed">
                  {selectedArtwork.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;